import React, { useState, useEffect } from "react";
import "./Settings.css";
import { generateInvite, getOrganization, getGmailAuthUrl, getGmailStatus, disconnectGmail, syncGmail } from "../../api";
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const InviteSection = () => {
  const [inviteLink, setInviteLink] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [supportEmail, setSupportEmail] = useState("");

  useEffect(() => {
    getOrganization().then(org => {
      if (org.inboundEmail) setSupportEmail(org.inboundEmail);
    }).catch(console.error);
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const data = await generateInvite();
      const link = `https://dashboard-starter-self.vercel.app?invite=${data.token}`;
      setInviteLink(link);
      setExpiresAt(data.expiresAt);
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(supportEmail);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  return (
    <div className="invite-section">
      {supportEmail && (
        <div className="invite-link-box" style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
          <div className="invite-link-header">
            <span className="invite-link-label" style={{ color: "#3b82f6" }}>📧 Your Support Email</span>
          </div>
          <div className="invite-link-row">
            <input readOnly value={supportEmail} className="invite-link-input" />
            <button className="invite-copy-btn" style={{ background: "#3b82f6" }} onClick={handleCopyEmail}>
              {copiedEmail ? "✅ Copied!" : "Copy"}
            </button>
          </div>
          <p className="invite-hint">Share this email with your customers. Emails sent here will appear as tickets in your inbox.</p>
        </div>
      )}
      <div className="invite-info">
        <p>Share an invite link with your teammates. Each link is valid for <strong>7 days</strong> and can only be used <strong>once</strong>.</p>
      </div>
      <button className="settings-save-btn" onClick={handleGenerate} disabled={generating}>
        {generating ? "Generating..." : "✨ Generate Invite Link"}
      </button>
      {inviteLink && (
        <div className="invite-link-box">
          <div className="invite-link-header">
            <span className="invite-link-label">🔗 Invite Link</span>
            {expiresAt && (
              <span className="invite-expires">
                Expires {new Date(expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
          <div className="invite-link-row">
            <input readOnly value={inviteLink} className="invite-link-input" />
            <button className="invite-copy-btn" onClick={handleCopy}>
              {copied ? "✅ Copied!" : "Copy"}
            </button>
          </div>
          <p className="invite-hint">Send this link to your teammate. They'll create an account and land straight in the app.</p>
        </div>
      )}
    </div>
  );
};

const EmailSection = ({ addToast }) => {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGmailStatus().then(setStatus).catch(console.error).finally(() => setLoading(false));
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmailConnected') === 'true') {
      addToast('Gmail connected successfully! 🎉', 'success');
      window.history.replaceState({}, '', window.location.pathname);
      getGmailStatus().then(setStatus).catch(console.error);
    }
  }, []);

  const handleConnect = async () => {
    try {
      const { url } = await getGmailAuthUrl();
      window.location.href = url;
    } catch (err) {
      addToast('Failed to start Gmail connection', 'error');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectGmail();
      setStatus({ gmailEmail: null, gmailConnected: false });
      addToast('Gmail disconnected', 'info');
    } catch (err) {
      addToast('Failed to disconnect', 'error');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncGmail();
      addToast('Inbox synced!', 'success');
    } catch (err) {
      addToast(err.message || 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem', color: '#9ca3af' }}>Loading...</div>;

  return (
    <div className="email-section">
      {status?.gmailConnected ? (
        <div className="email-connected">
          <div className="email-connected-header">
            <div className="email-status-dot" />
            <div>
              <div className="email-connected-title">Gmail Connected</div>
              <div className="email-connected-address">{status.gmailEmail}</div>
            </div>
            <span className="email-connected-badge">Active</span>
          </div>
          <div className="email-actions">
            <button className="email-sync-btn" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing...' : '🔄 Sync Now'}
            </button>
            <button className="email-disconnect-btn" onClick={handleDisconnect}>
              Disconnect
            </button>
          </div>
          <div className="email-info-box">
            <p>✅ Emails sent to <strong>{status.gmailEmail}</strong> will automatically appear as tickets. Replies go out from your Gmail address directly.</p>
          </div>
        </div>
      ) : (
        <div className="email-connect-form">
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700 }}>Connect your Gmail</h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              Emails sent to your Gmail will appear as tickets. Replies go out from your own Gmail address.
            </p>
            <button className="settings-save-btn" onClick={handleConnect} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px' }}>
              <img src="https://www.google.com/favicon.ico" width="16" height="16" alt="Google" />
              Connect Gmail
            </button>
          </div>
          <div className="email-help-box">
            <strong>How it works:</strong>
            <ol>
              <li>Click "Connect Gmail" — Google OAuth popup appears</li>
              <li>Select your Gmail account and click Allow</li>
              <li>Emails sent to your Gmail become tickets automatically</li>
              <li>When you reply, customer gets email from your Gmail address</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

const CannedResponsesSection = ({ addToast }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', body: '' });
  const [saving, setSaving] = useState(false);

  const token = sessionStorage.getItem('token');

  const fetchResponses = async () => {
    try {
      const res = await fetch(`${BASE_URL}/canned-responses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setResponses(Array.isArray(data) ? data : Array.isArray(data.cannedResponses) ? data.cannedResponses : []);
    } catch (err) {
      addToast('Failed to load canned responses', 'error');
      setResponses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResponses(); }, []);

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      addToast('Title and body are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `${BASE_URL}/canned-responses/${editingId}` : `${BASE_URL}/canned-responses`;
      const method = editingId ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save');
      addToast(editingId ? 'Updated successfully!' : 'Created successfully!', 'success');
      setForm({ title: '', body: '' });
      setEditingId(null);
      setShowForm(false);
      fetchResponses();
    } catch (err) {
      addToast('Failed to save canned response', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setForm({ title: r.title, body: r.body });
    setEditingId(r.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this canned response?')) return;
    try {
      await fetch(`${BASE_URL}/canned-responses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      addToast('Deleted', 'info');
      fetchResponses();
    } catch (err) {
      addToast('Failed to delete', 'error');
    }
  };

  const handleCancel = () => {
    setForm({ title: '', body: '' });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div style={{ padding: '2rem', color: '#9ca3af' }}>Loading...</div>;

  return (
    <div className="canned-responses-section">
      {!showForm ? (
        <>
          <button className="settings-save-btn" onClick={() => setShowForm(true)} style={{ marginBottom: '1.5rem' }}>
            + New Canned Response
          </button>
          {responses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>💬</div>
              <p>No canned responses yet. Create your first one!</p>
            </div>
          ) : (
            <div className="canned-list">
              {responses.map((r) => (
                <div key={r.id} className="canned-item">
                  <div className="canned-item-content">
                    <div className="canned-item-title">{r.title}</div>
                    <div className="canned-item-body">{r.body}</div>
                  </div>
                  <div className="canned-item-actions">
                    <button className="canned-edit-btn" onClick={() => handleEdit(r)}>Edit</button>
                    <button className="canned-delete-btn" onClick={() => handleDelete(r.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="canned-form">
          <h3 style={{ margin: '0 0 1.5rem', fontSize: '1rem', fontWeight: 600 }}>
            {editingId ? 'Edit Canned Response' : 'New Canned Response'}
          </h3>
          <div className="settings-form-group">
            <label>Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Welcome message, Refund policy..."
            />
          </div>
          <div className="settings-form-group">
            <label>Response Body</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Type your canned response here..."
              rows={6}
              style={{ width: '100%', padding: '0.75rem', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="settings-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button className="canned-delete-btn" onClick={handleCancel} style={{ padding: '0.6rem 1.2rem' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = ({ addToast }) => {
  const currentUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const [activeTab, setActiveTab] = useState("profile");

  const [profileForm, setProfileForm] = useState({
    name: currentUser.name || "",
    email: currentUser.email || "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const initials = profileForm.name
    ? profileForm.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "A";

  const handleProfileSave = async () => {
    if (!profileForm.name.trim()) { addToast("Name is required", "error"); return; }
    setSavingProfile(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/users/${currentUser.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: profileForm.name, email: profileForm.email }),
      });
      if (!res.ok) throw new Error("Failed to update profile");
      const updatedUser = { ...currentUser, name: profileForm.name, email: profileForm.email };
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      addToast("Profile updated successfully!", "success");
    } catch (err) {
      addToast(err.message || "Failed to update profile", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!passwordForm.currentPassword) { addToast("Enter your current password", "error"); return; }
    if (passwordForm.newPassword.length < 6) { addToast("New password must be at least 6 characters", "error"); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { addToast("Passwords don't match", "error"); return; }
    setSavingPassword(true);
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/users/${currentUser.id}/password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword }),
      });
      if (!res.ok) throw new Error("Current password is incorrect");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      addToast("Password changed successfully!", "success");
    } catch (err) {
      addToast(err.message || "Failed to change password", "error");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="Settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <span className="settings-sub">Manage your account and preferences</span>
      </div>

      <div className="settings-layout">
        <div className="settings-tabs">
          {[
            { key: "profile", label: "Profile", icon: "👤" },
            { key: "password", label: "Password", icon: "🔒" },
            { key: "preferences", label: "Preferences", icon: "⚙️" },
            { key: "team", label: "Team & Invites", icon: "👥" },
            { key: "email", label: "Email Inbox", icon: "📧" },
            { key: "canned", label: "Canned Responses", icon: "💬" },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`settings-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="settings-content">
          {activeTab === "profile" && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Profile Information</h2>
                <p>Update your name and email address</p>
              </div>
              <div className="profile-avatar-section">
                <div className="profile-avatar-large">{initials}</div>
                <div>
                  <div className="profile-avatar-name">{profileForm.name}</div>
                  <div className="profile-avatar-role">{currentUser.role || "AGENT"}</div>
                </div>
              </div>
              <div className="settings-form">
                <div className="settings-form-group">
                  <label>Full Name</label>
                  <input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} placeholder="Your full name" />
                </div>
                <div className="settings-form-group">
                  <label>Email Address</label>
                  <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} placeholder="your@email.com" />
                </div>
                <div className="settings-form-group">
                  <label>Role</label>
                  <input value={currentUser.role || "AGENT"} disabled className="disabled-input" />
                  <span className="field-hint">Role can only be changed by an admin</span>
                </div>
                <button className="settings-save-btn" onClick={handleProfileSave} disabled={savingProfile}>
                  {savingProfile ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "password" && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Change Password</h2>
                <p>Make sure your account is using a strong password</p>
              </div>
              <div className="settings-form">
                <div className="settings-form-group">
                  <label>Current Password</label>
                  <input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} placeholder="Enter current password" />
                </div>
                <div className="settings-form-group">
                  <label>New Password</label>
                  <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} placeholder="Min. 6 characters" />
                </div>
                <div className="settings-form-group">
                  <label>Confirm New Password</label>
                  <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} placeholder="Repeat new password" />
                </div>
                {passwordForm.newPassword && (
                  <div className="password-strength">
                    <div className="strength-bars">
                      {[1,2,3,4].map((i) => (
                        <div key={i} className="strength-bar" style={{ background: passwordForm.newPassword.length >= i * 3 ? i <= 1 ? "#dc2626" : i <= 2 ? "#d97706" : "#16a34a" : "#e5e7eb" }} />
                      ))}
                    </div>
                    <span className="strength-label">
                      {passwordForm.newPassword.length < 4 ? "Weak" : passwordForm.newPassword.length < 8 ? "Fair" : passwordForm.newPassword.length < 12 ? "Good" : "Strong"}
                    </span>
                  </div>
                )}
                <button className="settings-save-btn" onClick={handlePasswordSave} disabled={savingPassword}>
                  {savingPassword ? "Changing..." : "Change Password"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "preferences" && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Preferences</h2>
                <p>Customize your Agent CRM experience</p>
              </div>
              <div className="preferences-list">
                {[
                  { label: "Email notifications", sub: "Get notified when a ticket is assigned to you", key: "emailNotif", default: true },
                  { label: "Sound alerts", sub: "Play a sound when a new message arrives", key: "soundAlerts", default: false },
                  { label: "Show ticket previews", sub: "Show message preview in the inbox list", key: "ticketPreviews", default: true },
                  { label: "Compact inbox view", sub: "Show more tickets with a denser layout", key: "compactInbox", default: false },
                ].map((pref) => (
                  <div key={pref.key} className="preference-row">
                    <div className="preference-info">
                      <span className="preference-label">{pref.label}</span>
                      <span className="preference-sub">{pref.sub}</span>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" defaultChecked={pref.default} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                ))}
              </div>
              <div className="settings-card-header" style={{ marginTop: "2rem" }}>
                <h2>Danger Zone</h2>
                <p>Irreversible actions — proceed with caution</p>
              </div>
              <button className="danger-btn">Delete Account</button>
            </div>
          )}

          {activeTab === "team" && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Team & Invites</h2>
                <p>Invite teammates to join your workspace</p>
              </div>
              <InviteSection />
            </div>
          )}

          {activeTab === "email" && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Email Inbox</h2>
                <p>Connect your email so customer emails become tickets automatically</p>
              </div>
              <EmailSection addToast={addToast} />
            </div>
          )}

          {activeTab === "canned" && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Canned Responses</h2>
                <p>Save reply templates to use quickly in tickets</p>
              </div>
              <CannedResponsesSection addToast={addToast} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Settings;
